// Picks the folder to move items into. It opens on the folder they are listed in and lists the
// folders in it, each opened by choosing it, with a way back up towards the top of the drive. A
// folder being moved can't be moved into itself, so it is listed but can't be opened, which keeps
// every folder below it out of reach as well. Move here is held back while the dialog is on the
// folder everything already sits in.
//
// The dialog only picks; the drive moves the items, the same way it does for items dropped on a
// folder, and says how that went.
import PropTypes from 'prop-types'
import {useState} from 'react'

import BackIcon from '@mui/icons-material/ArrowBackRounded'
import ChevronIcon from '@mui/icons-material/ChevronRightRounded'
import Dialog from '@mui/material/Dialog'
import FolderIcon from '@mui/icons-material/FolderRounded'
import IconButton from '@mui/material/IconButton'
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {isChildOf, placeOf} from '../../lib/folderTools'

import './moveDialog.scss'

const title = entries => (entries.length === 1 ? `Move “${entries[0].name}”` : `Move ${entries.length} items`)

const byName = (one, other) => one.name.localeCompare(other.name)

const MoveDialog = ({entries, folders, sources, start, topName = 'My Drive', onClose, onMove}) => {
  const [placeId, setPlaceId] = useState(start)
  const byId = new Map(folders.map(entry => [entry.id, entry]))
  const place = byId.get(placeId) ?? null
  const parentId = place && placeOf(place.parentId, byId)
  const moving = new Set(entries.filter(({kind}) => kind === 'folder').map(({id}) => id))
  const children = folders.filter(entry => isChildOf(entry, place?.id ?? null, byId)).sort(byName)
  const alreadyThere = sources.every(source => source === (place?.id ?? null))

  return (
    <Dialog open onClose={onClose} aria-labelledby='move-dialog-title' slotProps={{paper: {className: 'move-dialog'}}}>
      <div className='dialog-head'>
        <MoveIcon />
        <h2 className='dialog-title' id='move-dialog-title'>{title(entries)}</h2>
      </div>

      <div className='move-place'>
        {place && (
          <IconButton
            className='move-back'
            aria-label={`Back to ${byId.get(parentId)?.name ?? topName}`}
            onClick={() => setPlaceId(parentId)}>
            <BackIcon fontSize='small' />
          </IconButton>
        )}
        <span className='move-place-name'>{place?.name ?? topName}</span>
      </div>

      {children.length ?
        <ul className='move-folders' aria-label={`Folders in ${place?.name ?? topName}`}>
          {children.map(({id, name}) => (
            <li key={id}>
              <button
                type='button'
                className='move-folder'
                disabled={moving.has(id)}
                title={moving.has(id) ? 'A folder can’t be moved into itself' : null}
                onClick={() => setPlaceId(id)}>
                <FolderIcon className='move-folder-glyph' />
                <span className='move-folder-name'>{name}</span>
                <ChevronIcon className='move-folder-chevron' />
              </button>
            </li>
          ))}
        </ul> :
        <Text className='move-empty'>No folders in here.</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-move' disabled={alreadyThere} onClick={() => onMove(place)}>Move here</BaseButton>
      </div>
    </Dialog>
  )
}

const folderShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  parentId: PropTypes.string
})

MoveDialog.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.oneOf(['folder', 'file']).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  // Every folder in the drive but the root folder.
  folders: PropTypes.arrayOf(folderShape).isRequired,
  // The folder each item sits in, or null for the top of the drive.
  sources: PropTypes.arrayOf(PropTypes.string).isRequired,
  // The folder to open on, or null for the top of the drive.
  start: PropTypes.string,
  // What the top is called: My Drive, or Locked when moving within it.
  topName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  // Handed the folder chosen, or null for the top of the drive.
  onMove: PropTypes.func.isRequired
}

export default MoveDialog
